// Header metrics: is the warehouse healthy, and what happened on the last run.
//
// The ordering is deliberate. Test OUTCOME comes first because it is the only
// thing that changes nightly and the only thing that can be bad; shape and
// coverage follow because they change when someone edits a model, not on a
// schedule.
//
// One honesty rule runs through this. Test results only exist once a dbt run
// publishes run_results.json, so before the first run this shows an explicit
// "waiting" state rather than zeros. Zero failures and no data are the two
// things a health header must never conflate.

import type { LineageMetrics, LineageStage } from "../types";

function relative(iso: string | null): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

// elapsed_seconds is the SUM across every dbt run that published results, so a
// full pipeline (Silver and Gold, per source) folds four runs into one number.
// Printing it as a bare "1854s" reads as one run taking half an hour, which is
// not what happened, so the count of runs is named alongside it.
function duration(seconds: number): string {
  if (seconds < 90) return `${seconds.toFixed(0)}s`;
  const mins = Math.round(seconds / 60);
  return mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

export function GraphMetrics({
  metrics,
  stages,
}: {
  metrics: LineageMetrics;
  stages: LineageStage[];
}) {
  const run = metrics.run;
  const runCount =
    Object.keys(run.by_scope ?? {}).length || run.layers_present?.length || 1;
  const runningLayers = stages.filter((stage) => stage.status === "running").length;
  const failedLayers = stages.filter((stage) => stage.status === "failed").length;
  const passRate =
    run.available && run.total > 0
      ? Math.round((run.passed / run.total) * 100)
      : null;
  const healthy = run.available && run.failed === 0;

  return (
    <div className="gm">
      <div className={`gm-card gm-hero ${run.available ? (healthy ? "is-ok" : "is-bad") : "is-wait"}`}>
        {run.available ? (
          <>
            <div className="gm-value">
              {run.passed}
              <span className="gm-of">/{run.total}</span>
            </div>
            <div className="gm-label">
              tests passed{passRate !== null ? ` (${passRate}%)` : ""}
            </div>
            <div className="gm-sub">
              {run.generated_at_utc ? `last run ${relative(run.generated_at_utc)}` : "last run"}
              {run.elapsed_seconds
                ? ` - ${runCount} ${runCount === 1 ? "run" : "runs"}, ${duration(run.elapsed_seconds)}`
                : ""}
            </div>
          </>
        ) : (
          <>
            <div className="gm-value gm-wait">--</div>
            <div className="gm-label">no test results yet</div>
            <div className="gm-sub">
              the next nightly run publishes them
            </div>
          </>
        )}
      </div>

      <Metric
        value={run.available ? run.failed : null}
        label={run.failed === 1 ? "test failing" : "tests failing"}
        tone={run.available && run.failed > 0 ? "bad" : "ok"}
      />
      <Metric
        value={run.available ? run.warned : null}
        label={run.warned === 1 ? "warning" : "warnings"}
        tone={run.available && run.warned > 0 ? "warn" : "plain"}
      />
      <Metric
        value={runningLayers}
        label={runningLayers === 1 ? "layer running" : "layers running"}
        tone={runningLayers > 0 ? "warn" : "plain"}
      />
      <Metric
        value={failedLayers}
        label={failedLayers === 1 ? "layer failed" : "layers failed"}
        tone={failedLayers > 0 ? "bad" : "ok"}
      />
      <Metric
        value={metrics.coverage_pct}
        suffix="%"
        label="have a test"
        tone={metrics.coverage_pct < 60 ? "warn" : "plain"}
      />
      <Metric
        value={metrics.tables_untested}
        label="untested"
        tone={metrics.tables_untested > 0 ? "warn" : "ok"}
      />
      <Metric value={metrics.tests_declared} label="tests defined" tone="plain" />
    </div>
  );
}

function Metric({
  value,
  label,
  tone,
  suffix,
}: {
  value: number | null;
  label: string;
  tone: "ok" | "bad" | "warn" | "plain";
  suffix?: string;
}) {
  return (
    <div className={`gm-card gm-${tone}`}>
      <div className="gm-value">
        {value === null ? <span className="gm-wait">--</span> : value}
        {value !== null && suffix ? <span className="gm-of">{suffix}</span> : null}
      </div>
      <div className="gm-label">{label}</div>
    </div>
  );
}
